import type { OnChangeFn, PaginationState } from "@tanstack/react-table";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type FC,
  type ReactElement,
} from "react";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import {
  useCompanyScopeContext,
  useEmitterListener,
  usePageContext,
} from "@ryde/redux";
import {
  activeViewIdAtomFamily,
  activeViewSF,
  buildTableGroupRowMapKey,
  GlobalAppEventEnum,
  gridExpandedAF,
  GROUPING_GRID_NO_GROUPS,
  isEntityServerGroupedPagination,
  isEntityServerSidePaginated,
  Log,
  NO_GROUPING_QS_VALUE,
  resolveGroupRowsCount,
  shouldDebugReactTable,
  tableHelpers,
  tableItemPaginationAF,
  tableRowsCountAtomFamily,
  tableRowsGroupServerCountSf,
  useGridExpandedCollapse,
  useRydeTable,
  type DtoFieldType,
  type IExtendableDto,
  type IGroupValue,
  type IRydeHeaderGroup,
  type IRydeRow,
  type RenderSubComponentModel,
} from "@ryde/shared";

import { TableItemContext, TableWrapperContext } from "../generic";
import { GridContext } from "../generic/table/grid-context";
import { GroupingArea } from "./grid.grouping-area";
import { GridTable } from "./grid.table";
import type { IGridRowProps } from "./grid.table.row";

interface IGridProps<T> {
  // TODO refactor couldn't be undefined
  groupingValue?:
    T[keyof T] | undefined | null | typeof GROUPING_GRID_NO_GROUPS;
  tableData: T[];
  tableIndex: number;
}

const GridComponent = <T extends IExtendableDto>({
  groupingValue,
  tableData,
}: IGridProps<T>): ReactElement | null => {
  const {
    columnGrouping,
    columnOrder,
    columnSorting,
    columnVisibility,
    setColumnGrouping,
    setColumnOrder,
    setColumnSorting,
    setColumnVisibility,
    renderSubComponent,
    GridRowRenderer,
    columnDefs,
    groupingField,
  } = useContext(TableWrapperContext);
  const { collapsed, setCollapsed } = useContext(TableItemContext);
  const { entity } = usePageContext();
  const { eventEmitter } = useCompanyScopeContext();
  const columns = useMemo(() => columnDefs, [columnDefs]);

  // Get valid column IDs from current column definitions
  const validColumnIds = useMemo(() => {
    return new Set(columns.map(col => col.id).filter(Boolean) as string[]);
  }, [columns]);

  const gridExpandedCollapse = useRecoilValue(gridExpandedAF(entity));
  const tableRowsCount = useRecoilValue(tableRowsCountAtomFamily(entity));
  const activeView = useRecoilValue(activeViewSF(entity));
  const setActiveViewId = useSetRecoilState(activeViewIdAtomFamily(entity));

  const { onExpandedChange } = useGridExpandedCollapse();
  // Same key as the row map: a blank group pages under "", never under the ungrouped grid's key.
  const paginationGroupingKey = buildTableGroupRowMapKey(
    groupingValue as DtoFieldType | typeof GROUPING_GRID_NO_GROUPS,
  );
  const [tableItemPagination, setTableItemPagination] = useRecoilState(
    tableItemPaginationAF([paginationGroupingKey, entity]),
  );
  const isServerPaginated = useMemo(
    () => isEntityServerSidePaginated(entity),
    [entity],
  );
  const hasServerGroupPagination = useMemo(
    () =>
      isServerPaginated &&
      isEntityServerGroupedPagination(entity) &&
      Boolean(
        columnGrouping?.length &&
        columnGrouping[0] &&
        columnGrouping[0] !== NO_GROUPING_QS_VALUE,
      ),
    [columnGrouping, entity, isServerPaginated],
  );
  const tableRowsGroupServerCount = useRecoilValue(
    tableRowsGroupServerCountSf([groupingValue, entity]),
  );

  const pageSizeResolved = useMemo(
    () => tableHelpers.getPageSize(columnGrouping),
    [columnGrouping],
  );

  const defaultPaginationState = useMemo(
    () => ({
      pageIndex: 0,
      pageSize: pageSizeResolved,
    }),
    [pageSizeResolved],
  );

  // Sanitize columnOrder to only include valid column IDs
  const sanitizedColumnOrder = useMemo(() => {
    if (!columnOrder?.length) {
      return columnOrder;
    }
    return columnOrder.filter(colId => validColumnIds.has(colId));
  }, [columnOrder, validColumnIds]);

  // Sanitize columnVisibility to only include valid column IDs
  const sanitizedColumnVisibility = useMemo(() => {
    if (!columnVisibility || !Object.keys(columnVisibility).length) {
      return columnVisibility;
    }
    const sanitized: typeof columnVisibility = {};
    Object.keys(columnVisibility).forEach(colId => {
      if (validColumnIds.has(colId)) {
        sanitized[colId] = columnVisibility[colId];
      }
    });
    return sanitized;
  }, [columnVisibility, validColumnIds]);

  const pagination = tableItemPagination ?? defaultPaginationState;

  const pageCount = useMemo(() => {
    if (hasServerGroupPagination) {
      const count = tableRowsGroupServerCount || 0;
      return Math.max(1, Math.ceil(count / pagination.pageSize));
    }

    if (isServerPaginated) {
      return Math.max(1, Math.ceil(tableRowsCount / pagination.pageSize));
    }

    return Math.ceil(tableData.length / pagination.pageSize) || 1;
  }, [
    hasServerGroupPagination,
    isServerPaginated,
    pagination.pageSize,
    tableData.length,
    tableRowsCount,
    tableRowsGroupServerCount,
  ]);

  const groupRowsCount = resolveGroupRowsCount({
    entity,
    serverGroupRowsCount: tableRowsGroupServerCount,
    loadedGroupRowsCount: tableData.length,
  });

  const toggleCollapse = useEvent(() => {
    setCollapsed?.(isCollapsed => !isCollapsed);
  });

  const unGroup = useEvent(() => {
    setColumnGrouping?.([NO_GROUPING_QS_VALUE]);

    if (
      activeView &&
      activeView.viewState.columnsGrouping?.at(0) !== NO_GROUPING_QS_VALUE
    ) {
      setActiveViewId(undefined);
    }
  });

  const resetPaginationState = useEvent((): void => {
    setTableItemPagination(defaultPaginationState);
  });

  const getRowIdCB = useCallback(
    (originalRow: IExtendableDto, _index: number) => {
      return String(originalRow?.id);
    },
    [],
  );

  const handlePaginationChange: OnChangeFn<PaginationState> = useEvent(
    updaterOrValue => {
      const current = tableItemPagination ?? defaultPaginationState;
      const next =
        typeof updaterOrValue === "function"
          ? updaterOrValue(current)
          : updaterOrValue;

      setTableItemPagination(next);

      const groupByField = columnGrouping?.[0];
      if (
        isServerPaginated &&
        isEntityServerGroupedPagination(entity) &&
        groupByField &&
        groupByField !== NO_GROUPING_QS_VALUE
      ) {
        eventEmitter?.emit(GlobalAppEventEnum.PAGINATION_CHANGE, {
          groupingValue: groupingValue as IGroupValue["value"],
          pagination: next,
        });
      }
    },
  );

  const table = useRydeTable({
    data: tableData,
    meta: { groupingField, groupingValue },
    columns,
    pageCount,
    state: {
      pagination,
      columnVisibility: sanitizedColumnVisibility,
      columnOrder: sanitizedColumnOrder,
      sorting: columnSorting,
      expanded: gridExpandedCollapse,
    },
    manualPagination: isServerPaginated,
    manualSorting: isServerPaginated,
    onExpandedChange,
    onPaginationChange: handlePaginationChange,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onSortingChange: setColumnSorting,
    getRowCanExpand: () => true,
    debugTable: shouldDebugReactTable(),
    autoResetPageIndex: false,
    autoResetExpanded: false,
    getRowId: getRowIdCB,
  });
  const rows = table.getRowModel().rows;

  const setPageIndex = useCallback(
    (value: number) => {
      table.setPageIndex(value);
    },
    [table],
  );

  const gridContextValue = useMemo(
    () => ({
      setPageIndex,
      pageCount,
      pageIndex: pagination.pageIndex,
    }),
    [pageCount, pagination.pageIndex, setPageIndex],
  );

  useEffect(() => {
    if (tableItemPagination?.pageSize !== defaultPaginationState.pageSize) {
      setTableItemPagination(defaultPaginationState);
    }
  }, [defaultPaginationState, setTableItemPagination, tableItemPagination]);

  useEmitterListener({
    event: GlobalAppEventEnum.RESET_PAGINATION,
    handler: () => {
      Log.debug(`Reset pagination global event`);
      resetPaginationState();
    },
  });

  useEmitterListener({
    event: GlobalAppEventEnum.SET_PAGINATION_TO_RIDE,
    handler: rideId => {
      const rowIndex = table
        ?.getSortedRowModel()
        .rows.findIndex(({ id }) => Number(id) === Number(rideId));

      if (rowIndex === undefined || rowIndex < 0) {
        return;
      }

      // `rowIndex` is zero-based, so it must not be incremented before dividing.
      const pageIndex = Math.floor(rowIndex / pageSizeResolved);
      setTableItemPagination(currVal => {
        if (!currVal) {
          return { ...defaultPaginationState, pageIndex };
        }

        return {
          ...currVal,
          pageIndex,
        };
      });
    },
  });

  return (
    <>
      {Boolean(columnGrouping?.length) &&
        columnGrouping[0] !== NO_GROUPING_QS_VALUE && (
          <GroupingArea
            groupRowsCount={groupRowsCount}
            ungroupFn={unGroup}
            toggleCollapseFn={toggleCollapse}
            collapsed={collapsed}
            groupingValue={groupingValue}
            columnName={columnGrouping[0]}
          />
        )}
      {!collapsed && (
        <GridContext.Provider value={gridContextValue}>
          <GridTable
            headerGroups={
              table.getHeaderGroups() as IRydeHeaderGroup<IExtendableDto>[]
            }
            rows={rows as IRydeRow<IExtendableDto>[]}
            renderSubComponent={
              renderSubComponent as
                RenderSubComponentModel<IExtendableDto> | undefined
            }
            GridRowRenderer={
              GridRowRenderer as FC<IGridRowProps<IExtendableDto>> | undefined
            }
            isColumnResizeEnabled
          />
        </GridContext.Provider>
      )}
    </>
  );
};

export const Grid = memo(GridComponent) as typeof GridComponent;
